import { toaster } from "@/components/ui/toaster"
import { Box, Button, Flex, Input, Stack, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

import { OrganizationsService } from "@/client"
import type { OrganizationPublic } from "@/client"

interface BrandingDisplayNameProps {
  organization: OrganizationPublic
}

export function BrandingDisplayName({
  organization,
}: BrandingDisplayNameProps) {
  const [displayName, setDisplayName] = useState(
    organization.display_name || "",
  )
  const queryClient = useQueryClient()

  const updateMutation = useMutation({
    mutationFn: (newDisplayName: string | null) => {
      return OrganizationsService.updateOrganizationBranding({
        orgId: organization.id,
        requestBody: { display_name: newDisplayName },
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["branding", organization.id] })
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
      toaster.create({
        title: "Display name updated successfully",
        type: "success",
        duration: 3000,
      })
    },
    onError: (error: Error) => {
      toaster.create({
        title: "Update failed",
        description: error.message || "Failed to update display name",
        type: "error",
        duration: 5000,
      })
    },
  })

  const handleSave = () => {
    const trimmed = displayName.trim()
    updateMutation.mutate(trimmed || null)
  }

  const handleReset = () => {
    setDisplayName("")
    updateMutation.mutate(null)
  }

  const hasChanges = displayName.trim() !== (organization.display_name || "")

  return (
    <Box>
      <VStack align="stretch" gap={4}>
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={2}>
            Display Name
          </Text>
          <Text fontSize="sm" color="gray.600" mb={3}>
            This name will be shown to students throughout the platform. If not
            set, the organization name will be used.
          </Text>

          <Stack gap={3}>
            <Input
              placeholder={`Default: ${organization.name}`}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={255}
              disabled={updateMutation.isPending}
            />

            <Flex gap={2}>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                disabled={!hasChanges || updateMutation.isPending}
                loading={updateMutation.isPending}
              >
                Save Changes
              </Button>

              {organization.display_name && (
                <Button
                  variant="outline"
                  colorScheme="gray"
                  onClick={handleReset}
                  disabled={updateMutation.isPending}
                >
                  Reset to Organization Name
                </Button>
              )}
            </Flex>
          </Stack>
        </Box>

        <Box
          bg="blue.50"
          p={3}
          borderRadius="md"
          borderLeft="3px solid"
          borderColor="blue.500"
        >
          <Text fontSize="sm" color="blue.900">
            <strong>Current display:</strong>{" "}
            {organization.display_name || organization.name}
          </Text>
        </Box>
      </VStack>
    </Box>
  )
}
